import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { AdminLayout } from './components/AdminLayout';
import { API_BASE_URL } from '../../config/apiConfig';

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
`;

const PageTitle = styled.h1`
  margin: 0;
  color: #2c3e50;
  font-size: 28px;
`;

const FiltersContainer = styled.div`
  display: flex;
  gap: 15px;
  margin-bottom: 20px;
  padding: 20px;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  flex-wrap: wrap;
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const FilterLabel = styled.label`
  font-size: 12px;
  font-weight: 600;
  color: #555;
  text-transform: uppercase;
`;

const FilterSelect = styled.select`
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  min-width: 150px;
  
  &:focus {
    outline: none;
    border-color: #3498db;
  }
`;

const FilterInput = styled.input`
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  min-width: 150px;
  
  &:focus {
    outline: none;
    border-color: #3498db;
  }
`;

const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-bottom: 20px;
`;

const StatCard = styled.div`
  background-color: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  text-align: center;
`;

const StatValue = styled.div`
  font-size: 24px;
  font-weight: bold;
  color: #2c3e50;
  margin-bottom: 5px;
`;

const StatLabel = styled.div`
  font-size: 12px;
  color: #7f8c8d;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const TableContainer = styled.div`
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  overflow: hidden;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHeader = styled.th`
  background-color: #f8f9fa;
  padding: 15px 12px;
  text-align: left;
  font-size: 12px;
  font-weight: 600;
  color: #2c3e50;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 2px solid #e9ecef;
  cursor: ${props => props.sortable ? 'pointer' : 'default'};
  
  &:hover {
    background-color: ${props => props.sortable ? '#e9ecef' : '#f8f9fa'};
  }
`;

const TableRow = styled.tr`
  border-bottom: 1px solid #e9ecef;
  
  &:hover {
    background-color: #f8f9fa;
  }
`;

const TableCell = styled.td`
  padding: 12px;
  font-size: 14px;
  color: #2c3e50;
  vertical-align: middle;
`;

const StatusBadge = styled.span`
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  
  ${props => {
    switch (props.status) {
      case 'success':
        return `
          background-color: #d4edda;
          color: #155724;
          border: 1px solid #c3e6cb;
        `;
      case 'pending':
        return `
          background-color: #fff3cd;
          color: #856404;
          border: 1px solid #ffeaa7;
        `;
      case 'failed':
        return `
          background-color: #f8d7da;
          color: #721c24;
          border: 1px solid #f5c6cb;
        `;
      default:
        return `
          background-color: #e2e3e5;
          color: #383d41;
          border: 1px solid #d6d8db;
        `;
    }
  }}
`;

const PaymentTypeBadge = styled.span`
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  
  ${props => {
    switch (props.type) {
      case 'subscription':
        return `
          background-color: #e7f3ff;
          color: #0066cc;
          border: 1px solid #b3d9ff;
        `;
      case 'product_order':
        return `
          background-color: #f0f9e7;
          color: #2d5a0b;
          border: 1px solid #d4edaa;
        `;
      case 'refund':
        return `
          background-color: #fdf2e9;
          color: #b85450;
          border: 1px solid #fab1a0;
        `;
      default:
        return `
          background-color: #e2e3e5;
          color: #383d41;
          border: 1px solid #d6d8db;
        `;
    }
  }}
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 60px 20px;
  flex-direction: column;
  gap: 20px;
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #3498db;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const LoadingText = styled.p`
  color: #7f8c8d;
  font-size: 16px;
`;

const ErrorContainer = styled.div`
  background-color: #fee;
  border: 1px solid #fcc;
  border-radius: 6px;
  padding: 15px;
  margin: 20px 0;
  color: #c33;
`;

const Pagination = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  background-color: #f8f9fa;
  border-top: 1px solid #e9ecef;
`;

const PaginationInfo = styled.div`
  font-size: 14px;
  color: #6c757d;
`;

const PaginationControls = styled.div`
  display: flex;
  gap: 10px;
`;

const PaginationButton = styled.button`
  padding: 8px 12px;
  border: 1px solid #dee2e6;
  background-color: white;
  color: #495057;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  
  &:hover:not(:disabled) {
    background-color: #e9ecef;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// API Functions
const fetchPayments = async (filters = {}, page = 1, limit = 20) => {
  try {
    const token = sessionStorage.getItem('admin-token');
    if (!token) {
      throw new Error('Authentication required');
    }

    // Handle timeout filter - if status is 'timeout', use timed out payments endpoint
    if (filters.status === 'timeout') {
      const queryParams = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        days: '30', // Show timeouts from last 30 days
        ...Object.fromEntries(Object.entries({
          ...filters,
          status: '' // Remove timeout status for the timeouts endpoint
        }).filter(([key, v]) => v !== '' && key !== 'status'))
      });

      const response = await fetch(`${API_BASE_URL}/admin/payments/timeouts?${queryParams}`, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token
        }
      });
      
      const result = await response.json();
      if (result.is_success) {
        return {
          payments: result.data.payments || [],
          pagination: {
            totalPages: result.data.pagination?.total_pages || 1,
            totalCount: result.data.pagination?.total_items || 0
          }
        };
      } else {
        throw new Error(result.error || 'Failed to fetch timed out payments');
      }
    }

    // Regular payments endpoint for other filters
    const queryParams = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      ...Object.fromEntries(Object.entries(filters).filter(([_, v]) => v !== ''))
    });

    const response = await fetch(`${API_BASE_URL}/admin/payments?${queryParams}`, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': token
      }
    });
    
    const result = await response.json();
    if (result.is_success) {
      return result.data;
    } else {
      throw new Error(result.error || 'Failed to fetch payments');
    }
  } catch (error) {
    console.error('Error fetching payments:', error);
    throw error;
  }
};

const fetchPaymentStats = async (filters = {}) => {
  try {
    const token = sessionStorage.getItem('admin-token');
    if (!token) {
      throw new Error('Authentication required');
    }

    // If timeout filter is active, get timeout statistics
    if (filters.status === 'timeout') {
      const response = await fetch(`${API_BASE_URL}/admin/payments/timeout-analysis?days=30`, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token
        }
      });
      
      const result = await response.json();
      if (result.is_success) {
        // Transform timeout analysis data to match payment stats format
        return {
          totalPayments: result.data.overview?.total_payments || 0,
          successfulPayments: result.data.overview?.successful_payments || 0,
          pendingPayments: result.data.at_risk_payments?.count || 0,
          failedPayments: result.data.overview?.timed_out_payments || 0,
          timedOutPayments: result.data.overview?.timed_out_payments || 0,
          totalRevenue: 0 // Not applicable for timeout view
        };
      } else {
        throw new Error(result.error || 'Failed to fetch timeout stats');
      }
    }

    // Regular stats endpoint for other filters
    const queryParams = new URLSearchParams({
      ...Object.fromEntries(Object.entries(filters).filter(([_, v]) => v !== ''))
    });

    const response = await fetch(`${API_BASE_URL}/admin/payments/stats?${queryParams}`, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': token
      }
    });
    
    const result = await response.json();
    if (result.is_success) {
      // Add timeout count to regular stats
      const baseStats = result.data;
      
      // Fetch timeout count separately for regular stats
      try {
        const timeoutResponse = await fetch(`${API_BASE_URL}/admin/payments/timeout-analysis?days=30`, {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': token
          }
        });
        const timeoutResult = await timeoutResponse.json();
        if (timeoutResult.is_success) {
          baseStats.timedOutPayments = timeoutResult.data.overview?.timed_out_payments || 0;
        }
      } catch (error) {
        console.error('Error fetching timeout count:', error);
        baseStats.timedOutPayments = 0;
      }
      
      return baseStats;
    } else {
      throw new Error(result.error || 'Failed to fetch payment stats');
    }
  } catch (error) {
    console.error('Error fetching payment stats:', error);
    throw error;
  }
};

export function PaymentManagement() {
  const [payments, setPayments] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [filters, setFilters] = useState({
    status: '',
    paymentType: '',
    startDate: '',
    endDate: '',
    searchTerm: ''
  });

  // Fetch payments on component mount and when filters change
  useEffect(() => {
    loadPayments();
  }, [currentPage, filters]);

  // Fetch stats separately
  useEffect(() => {
    loadStats();
  }, [filters]);

  const loadPayments = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchPayments(filters, currentPage, 20);
      setPayments(data.payments || []);
      setTotalPages(data.pagination?.totalPages || 1);
      setTotalCount(data.pagination?.totalCount || 0);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const data = await fetchPaymentStats(filters);
      setStats(data);
    } catch (err) {
      console.error('Error loading stats:', err);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1); // Reset to first page when filters change
  };

  const formatCurrency = (amount, currency = 'INR') => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <AdminLayout>
      <PageHeader>
        <PageTitle>Payment Management</PageTitle>
      </PageHeader>

      {/* Stats Cards */}
      {stats && (
        <StatsContainer>
          <StatCard>
            <StatValue>{formatCurrency(stats.totalRevenue || 0)}</StatValue>
            <StatLabel>Total Revenue</StatLabel>
          </StatCard>
          <StatCard>
            <StatValue>{stats.totalPayments || 0}</StatValue>
            <StatLabel>Total Payments</StatLabel>
          </StatCard>
          <StatCard>
            <StatValue>{stats.successfulPayments || 0}</StatValue>
            <StatLabel>Successful</StatLabel>
          </StatCard>
          <StatCard>
            <StatValue>{stats.pendingPayments || 0}</StatValue>
            <StatLabel>Pending</StatLabel>
          </StatCard>
          <StatCard>
            <StatValue>{stats.failedPayments || 0}</StatValue>
            <StatLabel>Failed</StatLabel>
          </StatCard>
          <StatCard>
            <StatValue>{stats.timedOutPayments || 0}</StatValue>
            <StatLabel>Timed Out</StatLabel>
          </StatCard>
        </StatsContainer>
      )}

      {/* Filters */}
      <FiltersContainer>
        <FilterGroup>
          <FilterLabel>Status</FilterLabel>
          <FilterSelect 
            value={filters.status} 
            onChange={(e) => handleFilterChange('status', e.target.value)}
          >
            <option value="">All Statuses</option>
            <option value="success">Success</option>
            <option value="pending">Pending</option>
            <option value="failed">Failed</option>
            <option value="processing">Processing</option>
            <option value="timeout">Timed Out</option>
          </FilterSelect>
        </FilterGroup>

        <FilterGroup>
          <FilterLabel>Payment Type</FilterLabel>
          <FilterSelect 
            value={filters.paymentType} 
            onChange={(e) => handleFilterChange('paymentType', e.target.value)}
          >
            <option value="">All Types</option>
            <option value="subscription">Subscription</option>
            <option value="product_order">Product Order</option>
            <option value="refund">Refund</option>
          </FilterSelect>
        </FilterGroup>

        <FilterGroup>
          <FilterLabel>Start Date</FilterLabel>
          <FilterInput 
            type="date"
            value={filters.startDate}
            onChange={(e) => handleFilterChange('startDate', e.target.value)}
          />
        </FilterGroup>

        <FilterGroup>
          <FilterLabel>End Date</FilterLabel>
          <FilterInput 
            type="date"
            value={filters.endDate}
            onChange={(e) => handleFilterChange('endDate', e.target.value)}
          />
        </FilterGroup>

        <FilterGroup>
          <FilterLabel>Search</FilterLabel>
          <FilterInput 
            type="text"
            placeholder="Payment ID, Order ID, User ID..."
            value={filters.searchTerm}
            onChange={(e) => handleFilterChange('searchTerm', e.target.value)}
          />
        </FilterGroup>
      </FiltersContainer>

      {error && (
        <ErrorContainer>
          <div>Error: {error}</div>
        </ErrorContainer>
      )}

      {/* Payments Table */}
      <TableContainer>
        {loading ? (
          <LoadingContainer>
            <LoadingSpinner />
            <LoadingText>Loading payments...</LoadingText>
          </LoadingContainer>
        ) : (
          <>
            <Table>
              <thead>
                <tr>
                  <TableHeader>Payment ID</TableHeader>
                  <TableHeader>Order ID</TableHeader>
                  <TableHeader>User</TableHeader>
                  <TableHeader>Amount</TableHeader>
                  <TableHeader>Type</TableHeader>
                  <TableHeader>Status</TableHeader>
                  <TableHeader>Method</TableHeader>
                  <TableHeader>Timeout Info</TableHeader>
                  <TableHeader>Date</TableHeader>
                </tr>
              </thead>
              <tbody>
                {payments.map(payment => (
                  <TableRow key={payment.paymentId}>
                    <TableCell>
                      <div style={{ fontFamily: 'monospace', fontSize: '12px' }}>
                        {payment.paymentId}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div style={{ fontFamily: 'monospace', fontSize: '12px' }}>
                        {payment.orderId}
                      </div>
                    </TableCell>
                                         <TableCell>
                       <div>
                         <div style={{ fontWeight: '500' }}>
                           {payment.user ? `${payment.user.firstname || ''} ${payment.user.lastname || ''}`.trim() || payment.user.email : 'N/A'}
                         </div>
                         <div style={{ fontSize: '11px', color: '#7f8c8d' }}>
                           {payment.userId}
                         </div>
                       </div>
                     </TableCell>
                    <TableCell>
                      <div style={{ fontWeight: '600' }}>
                        {formatCurrency(payment.amount, payment.currency)}
                      </div>
                    </TableCell>
                    <TableCell>
                      <PaymentTypeBadge type={payment.paymentType}>
                        {payment.paymentType.replace('_', ' ')}
                      </PaymentTypeBadge>
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={payment.status}>
                        {payment.status}
                      </StatusBadge>
                    </TableCell>
                    <TableCell>
                      {payment.paymentMethod || 'N/A'}
                    </TableCell>
                    <TableCell>
                      {payment.metadata?.auto_failed ? (
                        <div>
                          <div style={{ fontSize: '11px', color: '#e74c3c', fontWeight: '600' }}>
                            AUTO TIMEOUT
                          </div>
                          <div style={{ fontSize: '10px', color: '#7f8c8d' }}>
                            {payment.metadata.timeout_minutes}m
                          </div>
                        </div>
                      ) : payment.status === 'failed' && payment.failureReason?.includes('timed out') ? (
                        <div style={{ fontSize: '11px', color: '#f39c12', fontWeight: '600' }}>
                          TIMEOUT
                        </div>
                      ) : payment.status === 'pending' ? (
                        <div>
                          <div style={{ fontSize: '10px', color: '#7f8c8d' }}>
                            {Math.floor((Date.now() - new Date(payment.createdAt).getTime()) / (1000 * 60))}m old
                          </div>
                          {Math.floor((Date.now() - new Date(payment.createdAt).getTime()) / (1000 * 60)) > 12 && (
                            <div style={{ fontSize: '9px', color: '#f39c12' }}>
                              AT RISK
                            </div>
                          )}
                        </div>
                      ) : (
                        <div style={{ fontSize: '10px', color: '#7f8c8d' }}>
                          -
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <div style={{ fontSize: '12px' }}>
                        {formatDate(payment.createdAt)}
                      </div>
                      {payment.processedAt && (
                        <div style={{ fontSize: '10px', color: '#7f8c8d' }}>
                          Processed: {formatDate(payment.processedAt)}
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </tbody>
            </Table>

            {payments.length === 0 && !loading && (
              <div style={{ textAlign: 'center', padding: '40px', color: '#7f8c8d' }}>
                No payments found matching the current filters.
              </div>
            )}

            <Pagination>
              <PaginationInfo>
                Showing {((currentPage - 1) * 20) + 1} to {Math.min(currentPage * 20, totalCount)} of {totalCount} payments
              </PaginationInfo>
              <PaginationControls>
                <PaginationButton 
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                >
                  Previous
                </PaginationButton>
                <span style={{ padding: '8px 12px', fontSize: '14px' }}>
                  Page {currentPage} of {totalPages}
                </span>
                <PaginationButton 
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                >
                  Next
                </PaginationButton>
              </PaginationControls>
            </Pagination>
          </>
        )}
      </TableContainer>
    </AdminLayout>
  );
} 